<template>
    <div class="py-2 md:py-4 relative">
        <!-- All trips used message -->
        <div
            v-if="remainingTrips <= 0 && !showingAprilFoolsReveal"
            class="bg-white p-8 rounded-lg shadow text-center"
        >
            <h3 class="text-xl font-bold text-slate-800 mb-4">
                No more trips available
            </h3>
            <p class="text-slate-600 mb-4">
                You've used all your available trip swaps. Better luck next
                time!
            </p>
        </div>

        <!-- April Fool's Reveal using standard UI Modal -->
        <Teleport to="body">
            <UiModal
                :is-open="showingAprilFoolsReveal"
                title="April fool's!"
                :width="'670px'"
                :isTeleported="true"
                @close="closeReveal"
            >
                <template #content>
                    <div class="text-center">
                        <div
                            class="h-56 w-full"
                            style="
                                background-size: 85%;
                                background-position: center;
                                background-repeat: no-repeat;
                                background-image: url('/images/april-fools-modal-bg.png');
                            "
                        ></div>
                        <p class="text-slate-800 text-xl mb-3">
                            There's no trip swap. This was just a harmless April
                            Fool's joke!
                        </p>
                        <p class="text-slate-600 mb-4 max-w-lg mx-auto">
                            We hope you enjoyed our little prank! Sorry, but
                            you're stuck with your existing redemption. ;)
                        </p>
                        <div class="flex items-center justify-center gap-4">
                            <button
                                @click="closeReveal"
                                class="btn btn-orange px-6 py-2 rounded-lg"
                            >
                                Ok, got it!
                            </button>
                            <a
                                href="/"
                                class="btn btn-secondary px-6 py-2 rounded-lg"
                            >
                                Go to Redemption Tracker
                            </a>
                        </div>
                    </div>
                </template>
            </UiModal>
        </Teleport>
        <!-- Confetti now positioned outside but still activated by modal state -->
        <Confetti :fireConfetti="showingAprilFoolsReveal" />

        <!-- Two column layout for trip swap -->
        <div
            v-if="currentTrip && remainingTrips > 0"
            class="relative px-8"
            :class="{ 'opacity-20': showingAprilFoolsReveal }"
        >
            <div
                class="grid grid-cols-1 md:grid-cols-2 gap-1 p-1 bg-white rounded-xl relative shadow"
            >
                <LeftColumn :user-trip="userTrip" @changeTrip="changeTrip" />
                <RightColumn
                    :current-trip="currentTrip"
                    :user-trip="userTrip"
                    :is-generating-new-trip="isGeneratingNewTrip"
                    :is-checking-for-trips="isCheckingForTrips"
                    :no-trips-available="noTripsAvailable"
                    :show-success-notification="showSuccessNotification"
                    :show-failure-notification="showFailureNotification"
                    :countdown="countdown"
                    @generateNewTrip="generateNewTrip"
                    @acceptTrip="acceptTrip"
                    @checkForAvailableSwaps="checkForAvailableSwaps"
                    :timer-expired="timerExpired"
                    :remaining-trips="remainingTrips"
                    :showing-april-fools-reveal="showingAprilFoolsReveal"
                />
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, computed, onBeforeUnmount, nextTick } from 'vue';
import Confetti from '@/components/confetti.vue';
import UiModal from '@/components/ui/modal/ui-modal.vue';
import LeftColumn from '@/components/trip-swap/left-column.vue';
import RightColumn from '@/components/trip-swap/right-column.vue';
import { Apii } from '../../services/api';
const apiService = new Apii();
import axios from 'axios';

const programs = ref([]);
const isLoading = ref(false);
const userTrip = ref(null);
const isLoadingUserTrip = ref(false);

const mediocreTrips = [
    {
        id: 1,
        title: 'Thrilling Toledo, Ohio',
        start_date: '5/21/2025',
        end_date: '5/27/2025',
        image: {
            path: 'uploads/tripswap', // No leading or trailing slashes. This needs to also be on DO Spaces.
            file_name: 'leo-escala-lmqBIlLUKpU-unsplash.jpg', // Add your image filename
        },
        redemptions: [
            {
                start_date: '5/21/2025',
                end_date: '5/21/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 22500,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 5.6,
                number_of_travelers: 1,
                name: 'Flight to Toledo, Ohio',
                redemption_type: {
                    icon: 'flight', // flight, hotel, train, rental-car, other
                    slug: 'flight',
                },
                payment_program: {
                    name: 'United MileagePlus',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'Q4HKeOYCMrMTrmsQBi1UNXN7R8rHNorOgfYC8IIS.png',
                    },
                },
                date_type: 'start',
            },
            {
                start_date: '5/21/2025',
                end_date: '5/23/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 17500,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 0.0,
                number_of_travelers: 1,
                name: 'Hotel in Toledo, Ohio',
                redemption_type: {
                    icon: 'hotel', // flight, hotel, train, rental-car, other
                    slug: 'hotel',
                },
                payment_program: {
                    name: 'Hilton Honors',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'zcVUBO0ZLVWdTEifaEiXGMNQi5wHY6LPAALgurzA.png',
                    },
                },
                date_type: 'start',
            },
            {
                start_date: '5/23/2025',
                end_date: '5/25/2025',
                payment_type: 'cash', // points, cash, cash_points
                payment_points: 0,
                payment_cash_amount: 170.0,
                payment_taxes_fees: 35.0,
                number_of_travelers: 1,
                name: 'Rental Car in Toledo, Ohio',
                redemption_type: {
                    icon: 'rental-car', // flight, hotel, train, rental-car, other
                    slug: 'rental-car',
                },
                payment_program: {
                    name: 'Chase Ultimate Rewards',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'KHnGhfxvlUmugViE71GNgY3rIN9zZu0IHogInhab.png',
                    },
                },
                date_type: 'start',
            },
            {
                start_date: '5/25/2025',
                end_date: '5/25/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 22500,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 5.6,
                number_of_travelers: 1,
                name: 'Flight Home from Toledo, Ohio',
                redemption_type: {
                    icon: 'flight', // flight, hotel, train, rental-car, other
                    slug: 'flight',
                },
                payment_program: {
                    name: 'United MileagePlus',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'Q4HKeOYCMrMTrmsQBi1UNXN7R8rHNorOgfYC8IIS.png',
                    },
                },
                date_type: 'end',
            },
        ],
    },
    {
        id: 2,
        title: 'Exciting Bakersfield, California',
        start_date: '6/21/2025',
        end_date: '6/27/2025',
        image: {
            path: 'uploads/tripswap', // No leading or trailing slashes. This needs to also be on DO Spaces.
            file_name: 'tim-mossholder-qiraxliVTt0-unsplash.jpg', // Add your image filename
        },
        redemptions: [
            {
                start_date: '6/21/2025',
                end_date: '6/21/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 18750,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 5.6,
                number_of_travelers: 1,
                name: 'Flight to Bakersfield, California',
                redemption_type: {
                    icon: 'flight', // flight, hotel, train, rental-car, other
                    slug: 'flight',
                },
                payment_program: {
                    name: 'Southwest Rapid Rewards',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'UGSdJ3hJ3OhaaQz9N004b1G1iorF5Slt6Pqi8ACb.png',
                    },
                },
                date_type: 'start',
            },
            {
                start_date: '6/21/2025',
                end_date: '6/27/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 25000,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 0.0,
                number_of_travelers: 1,
                name: 'Hotel in Bakersfield, California',
                redemption_type: {
                    icon: 'hotel', // flight, hotel, train, rental-car, other
                    slug: 'hotel',
                },
                payment_program: {
                    name: 'Marriott Bonvoy',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'sGe7r2uVts5OTVaXJ7ppjWMQpN3vwXSSLoWeGkVD.png',
                    },
                },
                date_type: 'start',
            },
            {
                start_date: '6/27/2025',
                end_date: '6/27/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 22000,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 5.6,
                number_of_travelers: 1,
                name: 'Flight Home from Bakersfield, California',
                redemption_type: {
                    icon: 'flight', // flight, hotel, train, rental-car, other
                    slug: 'flight',
                },
                payment_program: {
                    name: 'Southwest Rapid Rewards',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'UGSdJ3hJ3OhaaQz9N004b1G1iorF5Slt6Pqi8ACb.png',
                    },
                },
                date_type: 'end',
            },
        ],
    },
    {
        id: 3,
        title: 'Breathtaking Gary, Indiana',
        start_date: '7/21/2025',
        end_date: '7/27/2025',
        image: {
            path: 'uploads/tripswap', // No leading or trailing slashes. This needs to also be on DO Spaces.
            file_name: 'lumin-osity-u0rXTqnUEgs-unsplash.jpg', // Add your image filename
        },
        redemptions: [
            {
                start_date: '7/21/2025',
                end_date: '7/21/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 32000,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 5.6,
                number_of_travelers: 1,
                name: 'Flight to Gary, Indiana',
                redemption_type: {
                    icon: 'flight', // flight, hotel, train, rental-car, other
                    slug: 'flight',
                },
                payment_program: {
                    name: 'American AAdvantage',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'DBM28vilGPpstKUIEtUJxehgQqBV7InVmIoXYAR5.png',
                    },
                },
                date_type: 'start',
            },
            {
                start_date: '7/21/2025',
                end_date: '7/23/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 10000,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 0.0,
                number_of_travelers: 1,
                name: 'Hotel in Gary, Indiana',
                redemption_type: {
                    icon: 'hotel', // flight, hotel, train, rental-car, other
                    slug: 'hotel',
                },
                payment_program: {
                    name: 'Hilton Honors',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'zcVUBO0ZLVWdTEifaEiXGMNQi5wHY6LPAALgurzA.png',
                    },
                },
                date_type: 'start',
            },
            {
                start_date: '7/23/2025',
                end_date: '7/25/2025',
                payment_type: 'cash', // points, cash, cash_points
                payment_points: 0,
                payment_cash_amount: 170.0,
                payment_taxes_fees: 35.0,
                number_of_travelers: 1,
                name: 'Rental Car in Gary, Indiana',
                redemption_type: {
                    icon: 'rental-car', // flight, hotel, train, rental-car, other
                    slug: 'rental-car',
                },
                payment_program: {
                    name: 'Chase Ultimate Rewards',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'KHnGhfxvlUmugViE71GNgY3rIN9zZu0IHogInhab.png',
                    },
                },
                date_type: 'start',
            },
            {
                start_date: '7/25/2025',
                end_date: '7/25/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 28500,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 5.6,
                number_of_travelers: 1,
                name: 'Flight Home from Gary, Indiana',
                redemption_type: {
                    icon: 'flight', // flight, hotel, train, rental-car, other
                    slug: 'flight',
                },
                payment_program: {
                    name: 'Delta SkyMiles',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'sgSHZ8pp3A4dd7cH0HnNMJN5ByvhdDYI9DHGw1aN.png',
                    },
                },
                date_type: 'end',
            },
        ],
    },
    {
        id: 4,
        title: 'Charming Fresno, California',
        start_date: '8/21/2025',
        end_date: '8/27/2025',
        image: {
            path: 'uploads/tripswap', // No leading or trailing slashes. This needs to also be on DO Spaces.
            file_name: 'grant-porter-Mx71xeQOev8-unsplash.jpg', // Add your image filename
        },
        redemptions: [
            {
                start_date: '8/21/2025',
                end_date: '8/21/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 25000,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 5.6,
                number_of_travelers: 1,
                name: 'Flight to Fresno, California',
                redemption_type: {
                    icon: 'flight', // flight, hotel, train, rental-car, other
                    slug: 'flight',
                },
                payment_program: {
                    name: 'Delta SkyMiles',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'sgSHZ8pp3A4dd7cH0HnNMJN5ByvhdDYI9DHGw1aN.png',
                    },
                },
                date_type: 'start',
            },
            {
                start_date: '8/21/2025',
                end_date: '8/27/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 10000,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 0.0,
                number_of_travelers: 1,
                name: 'Hotel in Fresno, California',
                redemption_type: {
                    icon: 'hotel', // flight, hotel, train, rental-car, other
                    slug: 'hotel',
                },
                payment_program: {
                    name: 'Hilton Honors',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'zcVUBO0ZLVWdTEifaEiXGMNQi5wHY6LPAALgurzA.png',
                    },
                },
                date_type: 'start',
            },
            {
                start_date: '8/27/2025',
                end_date: '8/27/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 25000,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 5.6,
                number_of_travelers: 1,
                name: 'Flight Home from Fresno, California',
                redemption_type: {
                    icon: 'flight', // flight, hotel, train, rental-car, other
                    slug: 'flight',
                },
                payment_program: {
                    name: 'United MileagePlus',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'Q4HKeOYCMrMTrmsQBi1UNXN7R8rHNorOgfYC8IIS.png',
                    },
                },
                date_type: 'end',
            },
        ],
    },
    {
        id: 5,
        title: 'Magical Minot, North Dakota',
        start_date: '5/21/2025',
        end_date: '5/27/2025',
        image: {
            path: 'uploads/tripswap', // No leading or trailing slashes. This needs to also be on DO Spaces.
            file_name: 'darla-hueske-Uz8xk0S_35c-unsplash.jpg', // Add your image filename
        },
        redemptions: [
            {
                start_date: '5/21/2025',
                end_date: '5/21/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 27500,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 5.6,
                number_of_travelers: 1,
                name: 'Flight to Minot, North Dakota',
                redemption_type: {
                    icon: 'flight', // flight, hotel, train, rental-car, other
                    slug: 'flight',
                },
                payment_program: {
                    name: 'Alaska Mileage Plan',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            '2xgU8wrmnV1dkn0uQL9Q1jvqXrFMHEeTIhivZqh7.png',
                    },
                },
                date_type: 'start',
            },
            {
                start_date: '5/21/2025',
                end_date: '5/27/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 13000,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 0.0,
                number_of_travelers: 1,
                name: 'Hotel in Minot, North Dakota',
                redemption_type: {
                    icon: 'hotel', // flight, hotel, train, rental-car, other
                    slug: 'hotel',
                },
                payment_program: {
                    name: 'Marriott Bonvoy',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'sGe7r2uVts5OTVaXJ7ppjWMQpN3vwXSSLoWeGkVD.png',
                    },
                },
                date_type: 'start',
            },
            {
                start_date: '5/27/2025',
                end_date: '5/27/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 30000,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 5.6,
                number_of_travelers: 1,
                name: 'Flight Home from Minot, North Dakota',
                redemption_type: {
                    icon: 'flight', // flight, hotel, train, rental-car, other
                    slug: 'flight',
                },
                payment_program: {
                    name: 'Alaska Mileage Plan',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            '2xgU8wrmnV1dkn0uQL9Q1jvqXrFMHEeTIhivZqh7.png',
                    },
                },
                date_type: 'end',
            },
        ],
    },
    {
        id: 6,
        title: 'Luxurious Lubbock, Texas',
        start_date: '4/15/2025',
        end_date: '4/20/2025',
        image: {
            path: 'uploads/tripswap', // No leading or trailing slashes. This needs to also be on DO Spaces.
            file_name: 'iStock-1423993954.jpg',
        },
        redemptions: [
            {
                start_date: '4/15/2025',
                end_date: '4/20/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 28000,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 0.0,
                number_of_travelers: 1,
                name: 'Flight to Lubbock, Texas',
                redemption_type: {
                    icon: 'flight', // flight, hotel, train, rental-car, other
                    slug: 'flight',
                },
                payment_program: {
                    name: 'American AAdvantage',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'DBM28vilGPpstKUIEtUJxehgQqBV7InVmIoXYAR5.png',
                    },
                },
                date_type: 'start',
            },
            {
                start_date: '4/15/2025',
                end_date: '4/20/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 50000,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 0.0,
                number_of_travelers: 1,
                name: 'Hotel in Lubbock, Texas',
                redemption_type: {
                    icon: 'hotel', // flight, hotel, train, rental-car, other
                    slug: 'hotel',
                },
                payment_program: {
                    name: 'Marriott Bonvoy',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'sGe7r2uVts5OTVaXJ7ppjWMQpN3vwXSSLoWeGkVD.png',
                    },
                },
                date_type: 'start',
            },
            {
                start_date: '4/20/2025',
                end_date: '4/20/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 31500,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 5.6,
                number_of_travelers: 1,
                name: 'Flight Home from Lubbock, Texas',
                redemption_type: {
                    icon: 'flight', // flight, hotel, train, rental-car, other
                    slug: 'flight',
                },
                payment_program: {
                    name: 'United MileagePlus',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'Q4HKeOYCMrMTrmsQBi1UNXN7R8rHNorOgfYC8IIS.png',
                    },
                },
                date_type: 'end',
            },
        ],
    },
    {
        id: 7,
        title: 'Breathtaking Butte, Montana',
        start_date: '6/10/2025',
        end_date: '6/15/2025',
        image: {
            path: 'uploads/tripswap', // No leading or trailing slashes. This needs to also be on DO Spaces.
            file_name: 'iStock-1642473522.jpg',
        },
        redemptions: [
            {
                start_date: '6/10/2025',
                end_date: '6/15/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 35000,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 0.0,
                number_of_travelers: 1,
                name: 'Flight to Butte, Montana',
                redemption_type: {
                    icon: 'flight', // flight, hotel, train, rental-car, other
                    slug: 'flight',
                },
                payment_program: {
                    name: 'Delta SkyMiles',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'sgSHZ8pp3A4dd7cH0HnNMJN5ByvhdDYI9DHGw1aN.png',
                    },
                },
                date_type: 'start',
            },
            {
                start_date: '6/10/2025',
                end_date: '6/15/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 20000,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 0.0,
                number_of_travelers: 1,
                name: 'Hotel in Butte, Montana',
                redemption_type: {
                    icon: 'hotel', // flight, hotel, train, rental-car, other
                    slug: 'hotel',
                },
                payment_program: {
                    name: 'Best Western Rewards',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'ptjiGriO4UPqashj6lnSz9qB6GJMRBDXOmhDDWmD.png',
                    },
                },
                date_type: 'start',
            },
            {
                start_date: '6/15/2025',
                end_date: '6/15/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 35000,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 5.6,
                number_of_travelers: 1,
                name: 'Flight Home from Butte, Montana',
                redemption_type: {
                    icon: 'flight', // flight, hotel, train, rental-car, other
                    slug: 'flight',
                },
                payment_program: {
                    name: 'American AAdvantage',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'DBM28vilGPpstKUIEtUJxehgQqBV7InVmIoXYAR5.png',
                    },
                },
                date_type: 'end',
            },
        ],
    },
    {
        id: 8,
        title: 'Romantic Rockford, Illinois',
        start_date: '7/5/2025',
        end_date: '7/10/2025',
        image: {
            path: 'uploads/tripswap', // No leading or trailing slashes. This needs to also be on DO Spaces.
            file_name: 'iStock-1127788354.jpg',
        },
        redemptions: [
            {
                start_date: '7/5/2025',
                end_date: '7/10/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 18000,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 0.0,
                number_of_travelers: 1,
                name: 'Flight to Rockford, Illinois',
                redemption_type: {
                    icon: 'flight', // flight, hotel, train, rental-car, other
                    slug: 'flight',
                },
                payment_program: {
                    name: 'Citi ThankYou Points',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'jMPrD7DvAbjZWxJU1175EDuAiiA7PDWIMsmnHjIt.png',
                    },
                },
                date_type: 'start',
            },
            {
                start_date: '7/5/2025',
                end_date: '7/10/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 15000,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 0.0,
                number_of_travelers: 1,
                name: 'Hotel in Rockford, Illinois',
                redemption_type: {
                    icon: 'hotel', // flight, hotel, train, rental-car, other
                    slug: 'hotel',
                },
                payment_program: {
                    name: 'Marriott Bonvoy',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'sGe7r2uVts5OTVaXJ7ppjWMQpN3vwXSSLoWeGkVD.png',
                    },
                },
                date_type: 'start',
            },
            {
                start_date: '7/10/2025',
                end_date: '7/10/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 20500,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 5.6,
                number_of_travelers: 1,
                name: 'Flight Home from Rockford, Illinois',
                redemption_type: {
                    icon: 'flight', // flight, hotel, train, rental-car, other
                    slug: 'flight',
                },
                payment_program: {
                    name: 'Citi ThankYou Points',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'jMPrD7DvAbjZWxJU1175EDuAiiA7PDWIMsmnHjIt.png',
                    },
                },
                date_type: 'end',
            },
        ],
    },
    {
        id: 9,
        title: 'Unforgettable Utica, New York',
        start_date: '5/15/2025',
        end_date: '5/20/2025',
        image: {
            path: 'uploads/tripswap', // No leading or trailing slashes. This needs to also be on DO Spaces.
            file_name: 'iStock-1420591301.jpg',
        },
        redemptions: [
            {
                start_date: '5/15/2025',
                end_date: '5/20/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 16500,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 0.0,
                number_of_travelers: 1,
                name: 'Flight to Utica, New York',
                redemption_type: {
                    icon: 'flight', // flight, hotel, train, rental-car, other
                    slug: 'flight',
                },
                payment_program: {
                    name: 'JetBlue TrueBlue',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'FTOch9NU0zr3Os9bTHwods1OTO0dQd97ixZHusHN.png',
                    },
                },
                date_type: 'start',
            },
            {
                start_date: '5/15/2025',
                end_date: '5/20/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 12000,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 0.0,
                number_of_travelers: 1,
                name: 'Hotel in Utica, New York',
                redemption_type: {
                    icon: 'hotel', // flight, hotel, train, rental-car, other
                    slug: 'hotel',
                },
                payment_program: {
                    name: 'Choice Privileges',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'YFi2cSlaN8OmZbgTTETpbHSqA2n4G23WICeteN6Z.png',
                    },
                },
                date_type: 'start',
            },
            {
                start_date: '5/20/2025',
                end_date: '5/20/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 16500,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 5.6,
                number_of_travelers: 1,
                name: 'Flight Home from Utica, New York',
                redemption_type: {
                    icon: 'flight', // flight, hotel, train, rental-car, other
                    slug: 'flight',
                },
                payment_program: {
                    name: 'JetBlue TrueBlue',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'FTOch9NU0zr3Os9bTHwods1OTO0dQd97ixZHusHN.png',
                    },
                },
                date_type: 'end',
            },
        ],
    },
    {
        id: 10,
        title: 'Picturesque Paducah, Kentucky',
        start_date: '8/1/2025',
        end_date: '8/6/2025',
        image: {
            path: 'uploads/tripswap', // No leading or trailing slashes. This needs to also be on DO Spaces.
            file_name: 'iStock-1161218344.jpg',
        },
        redemptions: [
            {
                start_date: '8/1/2025',
                end_date: '8/6/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 25000,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 0.0,
                number_of_travelers: 1,
                name: 'Flight to Paducah, Kentucky',
                redemption_type: {
                    icon: 'flight', // flight, hotel, train, rental-car, other
                    slug: 'flight',
                },
                payment_program: {
                    name: 'United MileagePlus',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'Q4HKeOYCMrMTrmsQBi1UNXN7R8rHNorOgfYC8IIS.png',
                    },
                },
                date_type: 'start',
            },
            {
                start_date: '8/1/2025',
                end_date: '8/6/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 8000,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 0.0,
                number_of_travelers: 1,
                name: 'Hotel in Paducah, Kentucky',
                redemption_type: {
                    icon: 'hotel', // flight, hotel, train, rental-car, other
                    slug: 'hotel',
                },
                payment_program: {
                    name: 'Wyndham Rewards',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            '8CNXbYj2YFU7jRbu3YOslUOHry18wkShCS6yVjJ5.png',
                    },
                },
                date_type: 'start',
            },
            {
                start_date: '8/6/2025',
                end_date: '8/6/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 24000,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 5.6,
                number_of_travelers: 1,
                name: 'Flight Home from Paducah, Kentucky',
                redemption_type: {
                    icon: 'flight', // flight, hotel, train, rental-car, other
                    slug: 'flight',
                },
                payment_program: {
                    name: 'American AAdvantage',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'DBM28vilGPpstKUIEtUJxehgQqBV7InVmIoXYAR5.png',
                    },
                },
                date_type: 'end',
            },
        ],
    },
    {
        id: 11,
        title: 'Enchanting Evansville, Indiana',
        start_date: '9/10/2025',
        end_date: '9/15/2025',
        image: {
            path: 'uploads/tripswap', // No leading or trailing slashes. This needs to also be on DO Spaces.
            file_name: 'iStock-518709892.jpg',
        },
        redemptions: [
            {
                start_date: '9/10/2025',
                end_date: '9/15/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 32000,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 0.0,
                number_of_travelers: 1,
                name: 'Flight to Evansville, Indiana',
                redemption_type: {
                    icon: 'flight', // flight, hotel, train, rental-car, other
                    slug: 'flight',
                },
                payment_program: {
                    name: 'American AAdvantage',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'DBM28vilGPpstKUIEtUJxehgQqBV7InVmIoXYAR5.png',
                    },
                },
                date_type: 'start',
            },
            {
                start_date: '9/1/2025',
                end_date: '9/1/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 32000,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 5.6,
                number_of_travelers: 1,
                name: 'Flight Home from Evansville, Indiana',
                redemption_type: {
                    icon: 'flight', // flight, hotel, train, rental-car, other
                    slug: 'flight',
                },
                payment_program: {
                    name: 'American AAdvantage',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'DBM28vilGPpstKUIEtUJxehgQqBV7InVmIoXYAR5.png',
                    },
                },
                date_type: 'end',
            },
        ],
    },
    {
        id: 12,
        title: 'Captivating Cedar Rapids, Iowa',
        start_date: '6/5/2025',
        end_date: '6/10/2025',
        image: {
            path: 'uploads/tripswap', // No leading or trailing slashes. This needs to also be on DO Spaces.
            file_name: 'iStock-1420755578.jpg',
        },
        redemptions: [
            {
                start_date: '6/5/2025',
                end_date: '6/10/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 22000,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 0.0,
                number_of_travelers: 1,
                name: 'Flight to Cedar Rapids, Iowa',
                redemption_type: {
                    icon: 'flight', // flight, hotel, train, rental-car, other
                    slug: 'flight',
                },
                payment_program: {
                    name: 'American AAdvantage',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'DBM28vilGPpstKUIEtUJxehgQqBV7InVmIoXYAR5.png',
                    },
                },
                date_type: 'start',
            },
            {
                start_date: '6/5/2025',
                end_date: '6/10/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 20000,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 0.0,
                number_of_travelers: 1,
                name: 'Hotel in Cedar Rapids, Iowa',
                redemption_type: {
                    icon: 'hotel', // flight, hotel, train, rental-car, other
                    slug: 'hotel',
                },
                payment_program: {
                    name: 'Hilton Honors',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'zcVUBO0ZLVWdTEifaEiXGMNQi5wHY6LPAALgurzA.png',
                    },
                },
                date_type: 'start',
            },
            {
                start_date: '6/10/2025',
                end_date: '6/10/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 19500,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 5.6,
                number_of_travelers: 1,
                name: 'Flight Home from Cedar Rapids, Iowa',
                redemption_type: {
                    icon: 'flight', // flight, hotel, train, rental-car, other
                    slug: 'flight',
                },
                payment_program: {
                    name: 'Southwest Rapid Rewards',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'UGSdJ3hJ3OhaaQz9N004b1G1iorF5Slt6Pqi8ACb.png',
                    },
                },
                date_type: 'end',
            },
        ],
    },
    {
        id: 13,
        title: 'Mysterious Modesto, California',
        start_date: '7/15/2025',
        end_date: '7/20/2025',
        image: {
            path: 'uploads/tripswap', // No leading or trailing slashes. This needs to also be on DO Spaces.
            file_name: 'iStock-2163130861.jpg',
        },
        redemptions: [
            {
                start_date: '7/15/2025',
                end_date: '7/20/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 28000,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 0.0,
                number_of_travelers: 1,
                name: 'Flight to Modesto, California',
                redemption_type: {
                    icon: 'flight', // flight, hotel, train, rental-car, other
                    slug: 'flight',
                },
                payment_program: {
                    name: 'Southwest Rapid Rewards',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'UGSdJ3hJ3OhaaQz9N004b1G1iorF5Slt6Pqi8ACb.png',
                    },
                },
                date_type: 'start',
            },
            {
                start_date: '7/15/2025',
                end_date: '7/20/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 25000,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 0.0,
                number_of_travelers: 1,
                name: 'Hotel in Modesto, California',
                redemption_type: {
                    icon: 'hotel', // flight, hotel, train, rental-car, other
                    slug: 'hotel',
                },
                payment_program: {
                    name: 'Marriott Bonvoy',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'sGe7r2uVts5OTVaXJ7ppjWMQpN3vwXSSLoWeGkVD.png',
                    },
                },
                date_type: 'start',
            },
            {
                start_date: '7/20/2025',
                end_date: '7/20/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 26000,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 5.6,
                number_of_travelers: 1,
                name: 'Flight Home from Modesto, California',
                redemption_type: {
                    icon: 'flight', // flight, hotel, train, rental-car, other
                    slug: 'flight',
                },
                payment_program: {
                    name: 'Southwest Rapid Rewards',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'UGSdJ3hJ3OhaaQz9N004b1G1iorF5Slt6Pqi8ACb.png',
                    },
                },
                date_type: 'end',
            },
        ],
    },
    {
        id: 14,
        title: 'Spectacular Shreveport, Louisiana',
        start_date: '8/20/2025',
        end_date: '8/25/2025',
        image: {
            path: 'uploads/tripswap', // No leading or trailing slashes. This needs to also be on DO Spaces.
            file_name: 'iStock-936136928.jpg',
        },
        redemptions: [
            {
                start_date: '8/20/2025',
                end_date: '8/25/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 30000,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 0.0,
                number_of_travelers: 1,
                name: 'Flight to Shreveport, Louisiana',
                redemption_type: {
                    icon: 'flight', // flight, hotel, train, rental-car, other
                    slug: 'flight',
                },
                payment_program: {
                    name: 'Delta SkyMiles',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'sgSHZ8pp3A4dd7cH0HnNMJN5ByvhdDYI9DHGw1aN.png',
                    },
                },
                date_type: 'start',
            },
            {
                start_date: '8/20/2025',
                end_date: '8/25/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 12000,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 0.0,
                number_of_travelers: 1,
                name: 'Hotel in Shreveport, Louisiana',
                redemption_type: {
                    icon: 'hotel', // flight, hotel, train, rental-car, other
                    slug: 'hotel',
                },
                payment_program: {
                    name: 'Wyndham Rewards',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            '8CNXbYj2YFU7jRbu3YOslUOHry18wkShCS6yVjJ5.png',
                    },
                },
                date_type: 'start',
            },
            {
                start_date: '8/25/2025',
                end_date: '8/25/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 25000,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 5.6,
                number_of_travelers: 1,
                name: 'Flight Home from Shreveport, Louisiana',
                redemption_type: {
                    icon: 'flight', // flight, hotel, train, rental-car, other
                    slug: 'flight',
                },
                payment_program: {
                    name: 'Delta SkyMiles',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'sgSHZ8pp3A4dd7cH0HnNMJN5ByvhdDYI9DHGw1aN.png',
                    },
                },
                date_type: 'end',
            },
        ],
    },
    {
        id: 15,
        title: 'Majestic Moline, Illinois',
        start_date: '5/25/2025',
        end_date: '5/30/2025',
        image: {
            path: 'uploads/tripswap', // No leading or trailing slashes. This needs to also be on DO Spaces.
            file_name: 'iStock-628951642.jpg',
        },
        redemptions: [
            {
                start_date: '5/25/2025',
                end_date: '5/30/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 18000,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 0.0,
                number_of_travelers: 1,
                name: 'Flight to Moline, Illinois',
                redemption_type: {
                    icon: 'flight', // flight, hotel, train, rental-car, other
                    slug: 'flight',
                },
                payment_program: {
                    name: 'United MileagePlus',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'Q4HKeOYCMrMTrmsQBi1UNXN7R8rHNorOgfYC8IIS.png',
                    },
                },
                date_type: 'start',
            },
            {
                start_date: '5/25/2025',
                end_date: '5/30/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 20000,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 0.0,
                number_of_travelers: 1,
                name: 'Hotel in Moline, Illinois',
                redemption_type: {
                    icon: 'hotel', // flight, hotel, train, rental-car, other
                    slug: 'hotel',
                },
                payment_program: {
                    name: 'IHG Rewards',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'H7M1lYHWwN1D7L3VHihQe2QzMk5VXmeBKCBQNrAv.png',
                    },
                },
                date_type: 'start',
            },
            {
                start_date: '5/30/2025',
                end_date: '5/30/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 28000,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 5.6,
                number_of_travelers: 1,
                name: 'Flight Home from Moline, Illinois',
                redemption_type: {
                    icon: 'flight', // flight, hotel, train, rental-car, other
                    slug: 'flight',
                },
                payment_program: {
                    name: 'American AAdvantage',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'DBM28vilGPpstKUIEtUJxehgQqBV7InVmIoXYAR5.png',
                    },
                },
                date_type: 'end',
            },
        ],
    },
    {
        id: 16,
        title: 'Paradise in Poughkeepsie, New York',
        start_date: '9/5/2025',
        end_date: '9/10/2025',
        image: {
            path: 'uploads/tripswap', // No leading or trailing slashes. This needs to also be on DO Spaces.
            file_name: 'iStock-1141541831.jpg',
        },
        redemptions: [
            {
                start_date: '9/5/2025',
                end_date: '9/10/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 20000,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 0.0,
                number_of_travelers: 1,
                name: 'Flight to Poughkeepsie, New York',
                redemption_type: {
                    icon: 'flight', // flight, hotel, train, rental-car, other
                    slug: 'flight',
                },
                payment_program: {
                    name: 'JetBlue TrueBlue',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'FTOch9NU0zr3Os9bTHwods1OTO0dQd97ixZHusHN.png',
                    },
                },
                date_type: 'start',
            },
            {
                start_date: '9/5/2025',
                end_date: '9/10/2025',
                payment_type: 'cash_points', // points, cash, cash_points
                payment_points: 0,
                payment_cash_amount: 150.0,
                payment_taxes_fees: 0.0,
                number_of_travelers: 1,
                name: 'Hotel in Poughkeepsie, New York',
                redemption_type: {
                    icon: 'hotel', // flight, hotel, train, rental-car, other
                    slug: 'hotel',
                },
                payment_program: {
                    name: 'Chase Ultimate Rewards',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'KHnGhfxvlUmugViE71GNgY3rIN9zZu0IHogInhab.png',
                    },
                },
                date_type: 'start',
            },
            {
                start_date: '9/10/2025',
                end_date: '9/10/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 16500,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 5.6,
                number_of_travelers: 1,
                name: 'Flight Home from Poughkeepsie, New York',
                redemption_type: {
                    icon: 'flight', // flight, hotel, train, rental-car, other
                    slug: 'flight',
                },
                payment_program: {
                    name: 'JetBlue TrueBlue',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'FTOch9NU0zr3Os9bTHwods1OTO0dQd97ixZHusHN.png',
                    },
                },
                date_type: 'end',
            },
        ],
    },
    {
        id: 17,
        title: 'Brilliant Boise, Idaho',
        start_date: '6/25/2025',
        end_date: '6/30/2025',
        image: {
            path: 'uploads/tripswap', // No leading or trailing slashes. This needs to also be on DO Spaces.
            file_name: 'iStock-1451155375.jpg',
        },
        redemptions: [
            {
                start_date: '6/25/2025',
                end_date: '6/30/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 25000,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 0.0,
                number_of_travelers: 1,
                name: 'Flight to Boise, Idaho',
                redemption_type: {
                    icon: 'flight', // flight, hotel, train, rental-car, other
                    slug: 'flight',
                },
                payment_program: {
                    name: 'Alaska Mileage Plan',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            '2xgU8wrmnV1dkn0uQL9Q1jvqXrFMHEeTIhivZqh7.png',
                    },
                },
                date_type: 'start',
            },
            {
                start_date: '6/25/2025',
                end_date: '6/30/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 22000,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 0.0,
                number_of_travelers: 1,
                name: 'Hotel in Boise, Idaho',
                redemption_type: {
                    icon: 'hotel', // flight, hotel, train, rental-car, other
                    slug: 'hotel',
                },
                payment_program: {
                    name: 'IHG Rewards',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'H7M1lYHWwN1D7L3VHihQe2QzMk5VXmeBKCBQNrAv.png',
                    },
                },
                date_type: 'start',
            },
            {
                start_date: '6/30/2025',
                end_date: '6/30/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 27500,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 5.6,
                number_of_travelers: 1,
                name: 'Flight Home from Boise, Idaho',
                redemption_type: {
                    icon: 'flight', // flight, hotel, train, rental-car, other
                    slug: 'flight',
                },
                payment_program: {
                    name: 'Alaska Mileage Plan',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            '2xgU8wrmnV1dkn0uQL9Q1jvqXrFMHEeTIhivZqh7.png',
                    },
                },
                date_type: 'end',
            },
        ],
    },
    {
        id: 18,
        title: 'Sensational Sioux Falls, South Dakota',
        start_date: '7/25/2025',
        end_date: '7/30/2025',
        image: {
            path: 'uploads/tripswap', // No leading or trailing slashes. This needs to also be on DO Spaces.
            file_name: 'iStock-2150541629.jpg',
        },
        redemptions: [
            {
                start_date: '7/25/2025',
                end_date: '7/30/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 18000,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 0.0,
                number_of_travelers: 1,
                name: 'Flight to Sioux Falls, South Dakota',
                redemption_type: {
                    icon: 'flight', // flight, hotel, train, rental-car, other
                    slug: 'flight',
                },
                payment_program: {
                    name: 'Citi ThankYou Points',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'jMPrD7DvAbjZWxJU1175EDuAiiA7PDWIMsmnHjIt.png',
                    },
                },
                date_type: 'start',
            },
            {
                start_date: '7/25/2025',
                end_date: '7/30/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 25000,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 0.0,
                number_of_travelers: 1,
                name: 'Hotel in Sioux Falls, South Dakota',
                redemption_type: {
                    icon: 'hotel', // flight, hotel, train, rental-car, other
                    slug: 'hotel',
                },
                payment_program: {
                    name: 'Marriott Bonvoy',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'sGe7r2uVts5OTVaXJ7ppjWMQpN3vwXSSLoWeGkVD.png',
                    },
                },
                date_type: 'start',
            },
            {
                start_date: '7/30/2025',
                end_date: '7/30/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 19500,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 5.6,
                number_of_travelers: 1,
                name: 'Flight Home from Sioux Falls, South Dakota',
                redemption_type: {
                    icon: 'flight', // flight, hotel, train, rental-car, other
                    slug: 'flight',
                },
                payment_program: {
                    name: 'United MileagePlus',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'Q4HKeOYCMrMTrmsQBi1UNXN7R8rHNorOgfYC8IIS.png',
                    },
                },
                date_type: 'end',
            },
        ],
    },
    {
        id: 19,
        title: 'Charming Chattanooga, Tennessee',
        start_date: '8/10/2025',
        end_date: '8/15/2025',
        image: {
            path: 'uploads/tripswap', // No leading or trailing slashes. This needs to also be on DO Spaces.
            file_name: 'iStock-1007834950.jpg',
        },
        redemptions: [
            {
                start_date: '8/10/2025',
                end_date: '8/15/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 22000,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 0.0,
                number_of_travelers: 1,
                name: 'Flight to Chattanooga, Tennessee',
                redemption_type: {
                    icon: 'flight', // flight, hotel, train, rental-car, other
                    slug: 'flight',
                },
                payment_program: {
                    name: 'American AAdvantage',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'DBM28vilGPpstKUIEtUJxehgQqBV7InVmIoXYAR5.png',
                    },
                },
                date_type: 'start',
            },
            {
                start_date: '8/10/2025',
                end_date: '8/15/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 40000,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 0.0,
                number_of_travelers: 1,
                name: 'Hotel in Chattanooga, Tennessee',
                redemption_type: {
                    icon: 'hotel', // flight, hotel, train, rental-car, other
                    slug: 'hotel',
                },
                payment_program: {
                    name: 'Hilton Honors',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'zcVUBO0ZLVWdTEifaEiXGMNQi5wHY6LPAALgurzA.png',
                    },
                },
                date_type: 'start',
            },
            {
                start_date: '8/15/2025',
                end_date: '8/15/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 24000,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 5.6,
                number_of_travelers: 1,
                name: 'Flight Home from Chattanooga, Tennessee',
                redemption_type: {
                    icon: 'flight', // flight, hotel, train, rental-car, other
                    slug: 'flight',
                },
                payment_program: {
                    name: 'American AAdvantage',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'DBM28vilGPpstKUIEtUJxehgQqBV7InVmIoXYAR5.png',
                    },
                },
                date_type: 'end',
            },
        ],
    },
    {
        id: 20,
        title: 'Fascinating Fargo, North Dakota',
        start_date: '5/5/2025',
        end_date: '5/10/2025',
        image: {
            path: 'uploads/tripswap', // No leading or trailing slashes. This needs to also be on DO Spaces.
            file_name: 'iStock-1836636190.jpg',
        },
        redemptions: [
            {
                start_date: '5/5/2025',
                end_date: '5/10/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 24000,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 0.0,
                number_of_travelers: 1,
                name: 'Flight to Fargo, North Dakota',
                redemption_type: {
                    icon: 'flight', // flight, hotel, train, rental-car, other
                    slug: 'flight',
                },
                payment_program: {
                    name: 'Delta SkyMiles',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'sgSHZ8pp3A4dd7cH0HnNMJN5ByvhdDYI9DHGw1aN.png',
                    },
                },
                date_type: 'start',
            },
            {
                start_date: '5/5/2025',
                end_date: '5/10/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 28000,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 0.0,
                number_of_travelers: 1,
                name: 'Hotel in Fargo, North Dakota',
                redemption_type: {
                    icon: 'hotel', // flight, hotel, train, rental-car, other
                    slug: 'hotel',
                },
                payment_program: {
                    name: 'Radisson Rewards',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'oXKQ4lVDmeeFipc6mfKNc98Oyn7nVWUMnkrDMrhD.png',
                    },
                },
                date_type: 'start',
            },
            {
                start_date: '5/10/2025',
                end_date: '5/10/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 22500,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 5.6,
                number_of_travelers: 1,
                name: 'Flight Home from Fargo, North Dakota',
                redemption_type: {
                    icon: 'flight', // flight, hotel, train, rental-car, other
                    slug: 'flight',
                },
                payment_program: {
                    name: 'Delta SkyMiles',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'sgSHZ8pp3A4dd7cH0HnNMJN5ByvhdDYI9DHGw1aN.png',
                    },
                },
                date_type: 'end',
            },
        ],
    },
    {
        id: 21,
        title: 'Radiant Reno, Nevada',
        start_date: '9/20/2025',
        end_date: '9/25/2025',
        image: {
            path: 'uploads/tripswap', // No leading or trailing slashes. This needs to also be on DO Spaces.
            file_name: 'iStock-1303751317.jpg',
        },
        redemptions: [
            {
                start_date: '9/20/2025',
                end_date: '9/25/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 20000,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 0.0,
                number_of_travelers: 1,
                name: 'Flight to Reno, Nevada',
                redemption_type: {
                    icon: 'flight', // flight, hotel, train, rental-car, other
                    slug: 'flight',
                },
                payment_program: {
                    name: 'Southwest Rapid Rewards',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'UGSdJ3hJ3OhaaQz9N004b1G1iorF5Slt6Pqi8ACb.png',
                    },
                },
                date_type: 'start',
            },
            {
                start_date: '9/20/2025',
                end_date: '9/25/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 12000,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 0.0,
                number_of_travelers: 1,
                name: 'Hotel in Reno, Nevada',
                redemption_type: {
                    icon: 'hotel', // flight, hotel, train, rental-car, other
                    slug: 'hotel',
                },
                payment_program: {
                    name: 'Chase Ultimate Rewards',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'KHnGhfxvlUmugViE71GNgY3rIN9zZu0IHogInhab.png',
                    },
                },
                date_type: 'start',
            },
            {
                start_date: '9/25/2025',
                end_date: '9/25/2025',
                payment_type: 'points', // points, cash, cash_points
                payment_points: 21500,
                payment_cash_amount: 0.0,
                payment_taxes_fees: 5.6,
                number_of_travelers: 1,
                name: 'Flight Home from Reno, Nevada',
                redemption_type: {
                    icon: 'flight', // flight, hotel, train, rental-car, other
                    slug: 'flight',
                },
                payment_program: {
                    name: 'Southwest Rapid Rewards',
                    program_image: {
                        path: 'uploads/program_images',
                        file_name:
                            'UGSdJ3hJ3OhaaQz9N004b1G1iorF5Slt6Pqi8ACb.png',
                    },
                },
                date_type: 'end',
            },
        ],
    },
];

const trips = ref([]);
const currentTrip = ref(null);
const remainingTrips = ref(20);
const showingAprilFoolsReveal = ref(false);

// Countdown timer functionality
const countdown = ref(0);
const timerExpired = ref(false);
const timerInterval = ref(null);
const remainingTime = ref(0);

// Generate a random number between min and max (inclusive)
const getRandomTime = (min, max) => {
    return Math.floor(Math.random() * (max - min + 1)) + min;
};

// Start the countdown timer with a random time between 21-48 seconds
const startCountdown = () => {
    // Set random time between 21 and 48 seconds
    countdown.value = getRandomTime(21, 48);
    timerExpired.value = false;
    clearInterval(timerInterval.value);

    console.log(`Timer started with ${countdown.value} seconds`);

    timerInterval.value = setInterval(() => {
        countdown.value--;
        if (countdown.value <= 0) {
            clearInterval(timerInterval.value);
            timerExpired.value = true;
        }
    }, 1000);
};

// Select a random trip that hasn't been shown yet
const selectRandomTrip = () => {
    // Get a random trip from the mediocre trips array
    const randomIndex = Math.floor(Math.random() * mediocreTrips.length);
    const trip = mediocreTrips[randomIndex];
    if (trip) {
        let tripImageUrl = `${cdnUrl.value}/${trip.image.path}/${trip.image.file_name}`;
        trip.imageUrl = tripImageUrl;
    }
    return trip;
};

// Track if no trips are available
const noTripsAvailable = ref(false);

// Track loading state when checking for available trips
const isCheckingForTrips = ref(false);
const isGeneratingNewTrip = ref(false);

// Track notification states
const showSuccessNotification = ref(false);
const showFailureNotification = ref(false);

// Clear all notifications
const clearNotifications = () => {
    showSuccessNotification.value = false;
    showFailureNotification.value = false;
};

// Generate a new trip when the user clicks "Generate Another Swap"
const generateNewTrip = async () => {
    try {
        closeTimerExpiredModal();
        isGeneratingNewTrip.value = true;
        const delay = getRandomTime(1000, 2000);

        await new Promise((resolve) => setTimeout(resolve, delay));

        const randomChance = Math.random();

        if (randomChance < 0.6) {
            noTripsAvailable.value = false;
            currentTrip.value = selectRandomTrip();
            if (currentTrip.value) {
                startCountdown();
            }
            logEvent('trip_swap_find_another');
        } else {
            // Failure: Show "no trips available" message
            noTripsAvailable.value = true;
            clearInterval(timerInterval.value);
            timerInterval.value = null;
            remainingTime.value = 0;
            logEvent('trip_swap_no_match');
        }
    } catch (error) {
        console.error('Error generating new trip:', error);
    } finally {
        await nextTick();
        isGeneratingNewTrip.value = false;
    }
};

const getRandomItem = (array) => {
    return array[Math.floor(Math.random() * array.length)];
};

const changeTrip = () => {
    const trip =
        trips.value.length > 1 ? getRandomItem(trips.value) : trips.value[0];
    userTrip.value = {
        id: trip.id,
        title: trip.title || trip.city || 'Your Next Adventure',
        start_date: trip.start_date,
        end_date: trip.end_date,
        redemptions: trip.redemptions || [],
    };
    loadTripImage(trip);
    logEvent('trip_swap_change');
};

// Check for available trip swaps
const checkForAvailableSwaps = () => {
    clearNotifications();
    isCheckingForTrips.value = true;

    setTimeout(() => {
        isCheckingForTrips.value = false;

        const randomChance = Math.random();

        if (randomChance < 0.6) {
            // Success: Generate a new trip
            noTripsAvailable.value = false;
            currentTrip.value = selectRandomTrip();
            if (currentTrip.value) {
                startCountdown();
                showSuccessNotification.value = true;
                setTimeout(() => {
                    showSuccessNotification.value = false;
                }, 5000); // Hide after 5 seconds
            }
        } else {
            noTripsAvailable.value = true;
            showFailureNotification.value = true;
            setTimeout(() => {
                showFailureNotification.value = false;
            }, 5000); // Hide after 5 seconds

            clearInterval(timerInterval.value);
            timerInterval.value = null;
            remainingTime.value = 0;
        }
    }, 1500); // 1.5 seconds loading time
};

const acceptTrip = () => {
    clearInterval(timerInterval.value);
    showingAprilFoolsReveal.value = true;
    logEvent('trip_swap_accept');
};

const closeReveal = () => {
    showingAprilFoolsReveal.value = false;
    generateNewTrip();
};

const closeTimerExpiredModal = () => {
    timerExpired.value = false;
};

// Utility function to load program data
const loadProgramData = async () => {
    isLoading.value = true;

    try {
        const response = await axios.get('/api/programs/all');

        programs.value = response.data;
    } catch (error) {
        console.error('Error loading program data from API:', error);
    } finally {
        isLoading.value = false;
    }
};

const cdnUrl = computed(() => {
    return window.cdnUrl;
});

const loadTripImage = (tripData) => {
    let tripImageUrl = null;
    if (
        tripData.image &&
        typeof tripData.image === 'object' &&
        tripData.image.path &&
        tripData.image.file_name
    ) {
        tripImageUrl = `${cdnUrl.value}/${tripData.image.path}/${tripData.image.file_name}`;
        userTrip.value.imageUrl = tripImageUrl;
    }

    userTrip.value = { ...userTrip.value };

    return tripImageUrl;
};

const loadUserTrip = async () => {
    isLoadingUserTrip.value = true;
    try {
        let tripData = null;

        try {
            const response = await apiService.apiCall('/trips/upcoming', 'get');

            if (
                response.data &&
                response.data.upcoming_trips &&
                response.data.upcoming_trips.data &&
                response.data.upcoming_trips.data.length > 0
            ) {
                // Handle paginated response structure - randomly select a trip if multiple exist
                trips.value = response.data.upcoming_trips.data;
                tripData =
                    trips.value.length > 1
                        ? getRandomItem(trips.value)
                        : trips.value[0];

                userTrip.value = {
                    id: tripData.id,
                    title: tripData.title || tripData.city,
                    start_date: tripData.start_date,
                    end_date: tripData.end_date,
                    redemptions: tripData.redemptions || [],
                };
            }
            // Check for direct upcoming_trips array
        } catch (error) {
            console.error('Error with API service call:', error);
        }

        // If no trip data was found, use a default placeholder
        if (!tripData) {
            isLoadingUserTrip.value = false;
            return;
        }

        // Process the trip data if we have it
        if (tripData) {
            loadTripImage(tripData);
        }
    } catch (error) {
        console.error('Error loading user trip:', error);
    } finally {
        isLoadingUserTrip.value = false;
    }
};

const logEvent = (eventType) => {
    axios.get('/trip-swap/log-event?event_type=' + eventType);
};

onBeforeUnmount(() => {
    if (timerInterval.value) {
        clearInterval(timerInterval.value);
    }
});

onMounted(() => {
    // Load both program data and user trip data when component mounts
    Promise.all([loadUserTrip(), loadProgramData()]).then(() => {
        // Initialize the trip and start the countdown
        try {
            // Set the initial trip
            currentTrip.value = selectRandomTrip();
            remainingTrips.value = 20;
            timerExpired.value = false;
            // Start the countdown timer with a random time immediately
            startCountdown();
        } catch (error) {
            console.error('Error initializing trip data:', error);
            const errorEl = document.createElement('div');
            errorEl.className =
                'bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6';
            errorEl.innerHTML = `
                <p class="font-bold">Error Initializing Trip Swap</p>
                <p>${error.message}</p>
            `;
            document.body.appendChild(errorEl);
        }
    });
});
</script>

<style scoped>
@keyframes pulse-fast {
    0%,
    100% {
        opacity: 1;
        transform: scale(1);
    }
    50% {
        opacity: 0.8;
        transform: scale(1.1);
    }
}

.animate-pulse-fast {
    animation: pulse-fast 0.6s ease-in-out infinite;
}

@keyframes confetti-rain {
    0% {
        transform: translateY(-10vh) rotate(0deg);
        opacity: 1;
    }
    100% {
        transform: translateY(100vh) rotate(720deg);
        opacity: 0;
    }
}
</style>
